/**
 * Auth service for authentication-related API calls.
 * Extracted from hooks to enable service-layer testing and mocking.
 */

import { ENV } from '../config/env'
import type { ApiResponseEnvelope } from './api'
import { authenticatedFetch } from './auth-interceptor'
import type { AvatarConfig } from '@/types/avatar'

const API_BASE_URL = ENV.API_BASE_URL

export interface ApiAuthResponse {
    access_token: string
    refresh_token?: string
}

export interface ApiUserResponse {
    id: string
    username: string
    email: string
    display_name?: string | null
    avatar_url?: string | null
    avatar_config?: AvatarConfig | null
    user_lang?: string | null
    target_langs?: string[]
}

export interface ApiErrorResponse {
    message?: string
    error?: string
    code?: string
    translation_key?: string
}

export interface LoginRequest {
    username: string
    password: string
}

export interface RegisterRequest {
    username: string
    email: string
    password: string
    user_lang: string
    target_langs: string[]
    invite_key?: string
}

export class RegisterError extends Error {
    code: string
    constructor(code: string, message: string) {
        super(message)
        this.name = 'RegisterError'
        this.code = code
    }
}

export interface UpdateProfileRequest {
    display_name?: string | null
    avatar_url?: string | null
    avatar_config?: AvatarConfig | null
}

export interface UpdateLanguageSettingsRequest {
    user_lang?: string
    primary_target_lang: string
    target_langs: string[]
}

export class AuthService {
    private baseURL: string

    constructor(baseURL: string = API_BASE_URL) {
        this.baseURL = baseURL
    }

    /**
     * Login with username and password.
     */
    async login(request: LoginRequest): Promise<ApiAuthResponse> {
        const response = await fetch(`${this.baseURL}/api/v1/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        })

        const responseData = (await response.json()) as
            ApiErrorResponse | ApiResponseEnvelope<ApiAuthResponse>

        if (!response.ok) {
            const errorData = responseData as ApiErrorResponse
            throw new Error(errorData.message || 'Login failed')
        }

        // Unwrap response envelope
        const data = 'data' in responseData ? responseData.data : (responseData as ApiAuthResponse)
        return data
    }

    /**
     * Register a new user.
     */
    async register(request: RegisterRequest): Promise<ApiAuthResponse> {
        const response = await fetch(`${this.baseURL}/api/v1/auth/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        })

        const responseData = (await response.json()) as
            ApiErrorResponse | ApiResponseEnvelope<ApiAuthResponse>

        if (!response.ok) {
            const errorData = responseData as ApiErrorResponse
            throw new RegisterError(
                errorData.error || errorData.code || 'UNKNOWN',
                errorData.message || 'Registration failed'
            )
        }

        // Unwrap response envelope
        const data = 'data' in responseData ? responseData.data : (responseData as ApiAuthResponse)
        return data
    }

    /**
     * Get current user info (requires authentication).
     */
    async getCurrentUser(): Promise<ApiUserResponse> {
        const response = await authenticatedFetch(`${this.baseURL}/api/v1/auth/me`, {
            headers: {
                'Content-Type': 'application/json',
            },
        })

        if (!response.ok) {
            const errorData = (await response.json().catch(() => ({}))) as ApiErrorResponse
            throw new Error(errorData.message || 'Failed to get user info')
        }

        const responseData = (await response.json()) as ApiResponseEnvelope<ApiUserResponse>
        // Unwrap response envelope
        if ('data' in responseData) {
            return responseData.data
        }
        return responseData
    }

    /**
     * Update user profile (requires authentication).
     */
    async updateProfile(request: UpdateProfileRequest): Promise<ApiUserResponse> {
        const response = await authenticatedFetch(`${this.baseURL}/api/v1/auth/profile`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        })

        const responseData = (await response.json()) as
            ApiErrorResponse | ApiResponseEnvelope<ApiUserResponse>

        if (!response.ok) {
            const errorData = responseData as ApiErrorResponse
            throw new Error(errorData.message || 'Profile update failed')
        }

        // Unwrap response envelope
        const data = 'data' in responseData ? responseData.data : responseData
        return data as ApiUserResponse
    }

    /**
     * Partially update user profile (requires authentication).
     * Only sends changed fields in the request.
     */
    async patchProfile(request: UpdateProfileRequest): Promise<ApiUserResponse> {
        const response = await authenticatedFetch(`${this.baseURL}/api/v1/auth/profile`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        })

        const responseData = (await response.json()) as
            ApiErrorResponse | ApiResponseEnvelope<ApiUserResponse>

        if (!response.ok) {
            const errorData = responseData as ApiErrorResponse
            throw new Error(errorData.message || 'Profile update failed')
        }

        // Unwrap response envelope
        const data = 'data' in responseData ? responseData.data : responseData
        return data as ApiUserResponse
    }

    /**
     * Update language settings (requires authentication).
     */
    async updateLanguageSettings(request: UpdateLanguageSettingsRequest): Promise<ApiUserResponse> {
        const response = await authenticatedFetch(`${this.baseURL}/api/v1/auth/language-settings`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        })

        const responseData = (await response.json()) as
            ApiErrorResponse | ApiResponseEnvelope<ApiUserResponse>

        if (!response.ok) {
            const errorData = responseData as ApiErrorResponse
            throw new Error(errorData.message || 'Language settings update failed')
        }

        // Unwrap response envelope
        const data = 'data' in responseData ? responseData.data : responseData
        return data as ApiUserResponse
    }
}

/**
 * Factory function to create a new AuthService instance.
 * Use this in tests for fresh, isolated instances.
 */
export const createAuthService = (baseURL?: string): AuthService => new AuthService(baseURL)

/**
 * Default singleton instance for production use.
 */
export const authService = createAuthService()
